/**
 * TrueSkill Rating Calculator Implementation
 * Based on Microsoft's TrueSkill algorithm for multiplayer rating systems
 */

import {
  DEFAULT_MU,
  DEFAULT_SIGMA,
  BETA,
  MAX_SCORE,
  MARGIN_SCALE_FACTOR,
} from '../../constants/gameConstants.js';

class TrueSkillCalculator {
  constructor() {
    this.mu = DEFAULT_MU; // Default mean skill: 25.0
    this.sigma = DEFAULT_SIGMA; // Default std deviation: 8.333
    this.beta = BETA; // Performance std deviation: 4.166
    this.tau = this.sigma / 100; // Dynamics factor: ~0.083
  }

  /**
   * Creates a new player rating
   */
  createRating(mu = this.mu, sigma = this.sigma) {
    return { mu, sigma };
  }

  /**
   * Calculate conservative rating (displayed rating)
   */
  getConservativeRating(mu, sigma) {
    const { calculateConservativeRating } = require('../rating');
    return calculateConservativeRating(mu, sigma);
  }

  /**
   * Normal cumulative distribution function
   */
  normCdf(x, mu = 0, sigma = 1) {
    const z = (x - mu) / sigma;
    const t = 1 / (1 + 0.2316419 * Math.abs(z));
    const d = 0.3989423 * Math.exp((-z * z) / 2);
    const probability =
      d *
      t *
      (0.3193815 +
        t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    return z > 0 ? 1 - probability : probability;
  }

  /**
   * Normal probability density function
   */
  normPdf(x, mu = 0, sigma = 1) {
    return (
      Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2)) /
      (sigma * Math.sqrt(2 * Math.PI))
    );
  }

  /**
   * Calculate team performance statistics
   */
  calculateTeamStats(teamRatings) {
    const teamMu = teamRatings.reduce((sum, rating) => sum + rating.mu, 0);
    const teamSigmaSquared = teamRatings.reduce(
      (sum, rating) => sum + rating.sigma * rating.sigma,
      0
    );

    return {
      mu: teamMu,
      sigma: Math.sqrt(
        teamSigmaSquared + teamRatings.length * this.beta * this.beta
      ),
    };
  }

  /**
   * Calculate match outcome probability
   */
  calculateDrawProbability(team1Stats, team2Stats) {
    const performanceDiff = team1Stats.mu - team2Stats.mu;
    const performanceVariance =
      team1Stats.sigma * team1Stats.sigma + team2Stats.sigma * team2Stats.sigma;

    return this.normCdf(0, performanceDiff, Math.sqrt(performanceVariance));
  }

  /**
   * Calculate margin multiplier based on score difference
   * Tiered system: 10-0 = 3x, 10-1 = 2x, others use 70% scale
   * @param {number} winnerScore - Winner's score
   * @param {number} loserScore - Loser's score
   * @returns {number} Multiplier for rating change
   */
  calculateMarginMultiplier(winnerScore, loserScore) {
    if (winnerScore === undefined || loserScore === undefined) return 1.0;
    const margin = Math.abs(winnerScore - loserScore);

    // Tiered system for dominant victories
    if (margin === 10) return 3.0; // 10-0 = 3x (like losing 3 games)
    if (margin === 9) return 2.0; // 10-1 = 2x (like losing 2 games)

    // Normal margin scaling for 10-2 through 10-9
    const normalizedMargin = Math.min(margin / MAX_SCORE, 1.0);
    return 1.0 + normalizedMargin * MARGIN_SCALE_FACTOR;
  }

  /**
   * Update ratings for a 2v2 match result
   * @param {Array} winners - Array of winner rating objects
   * @param {Array} losers - Array of loser rating objects
   * @param {number} winnerScore - Winner's score (optional)
   * @param {number} loserScore - Loser's score (optional)
   * @returns {Object} Updated ratings for winners and losers
   */
  rate2v2Match(winners, losers, winnerScore, loserScore) {
    // Calculate team statistics
    const winnerStats = this.calculateTeamStats(winners);
    const loserStats = this.calculateTeamStats(losers);

    // Calculate performance difference
    const performanceDiff = winnerStats.mu - loserStats.mu;
    const performanceVariance =
      winnerStats.sigma * winnerStats.sigma +
      loserStats.sigma * loserStats.sigma;
    const c = Math.sqrt(performanceVariance);

    // Calculate v and w functions (key TrueSkill calculations)
    const t = performanceDiff / c;
    const vt = this.normPdf(t) / this.normCdf(t);
    const wt = vt * (vt + t);

    // Calculate margin multiplier for score-based weighting
    const marginMultiplier = this.calculateMarginMultiplier(
      winnerScore,
      loserScore
    );

    // Calculate rating updates
    const updatedWinners = winners.map(rating => {
      const sigmaSquared = rating.sigma * rating.sigma;
      const meanMultiplier = (sigmaSquared + this.tau * this.tau) / c;
      const varianceMultiplier = (sigmaSquared + this.tau * this.tau) / (c * c);

      const baseMuChange = meanMultiplier * vt;
      const newMu = rating.mu + baseMuChange * marginMultiplier;
      const newSigmaSquared =
        (sigmaSquared + this.tau * this.tau) *
        Math.max(1 - varianceMultiplier * wt, 0.0001);

      return {
        mu: newMu,
        sigma: Math.sqrt(newSigmaSquared),
      };
    });

    const updatedLosers = losers.map(rating => {
      const sigmaSquared = rating.sigma * rating.sigma;
      const meanMultiplier = (sigmaSquared + this.tau * this.tau) / c;
      const varianceMultiplier = (sigmaSquared + this.tau * this.tau) / (c * c);

      const baseMuChange = meanMultiplier * vt;
      const newMu = rating.mu - baseMuChange * marginMultiplier;
      const newSigmaSquared =
        (sigmaSquared + this.tau * this.tau) *
        Math.max(1 - varianceMultiplier * wt, 0.0001);

      return {
        mu: newMu,
        sigma: Math.sqrt(newSigmaSquared),
      };
    });

    return {
      winners: updatedWinners,
      losers: updatedLosers,
    };
  }

  /**
   * Calculate quality of match (how fair/interesting the match is)
   * Higher values indicate more balanced matches
   */
  calculateMatchQuality(team1Ratings, team2Ratings) {
    const team1Stats = this.calculateTeamStats(team1Ratings);
    const team2Stats = this.calculateTeamStats(team2Ratings);

    const performanceVariance =
      team1Stats.sigma * team1Stats.sigma + team2Stats.sigma * team2Stats.sigma;
    const performanceDiff = team1Stats.mu - team2Stats.mu;

    return (
      Math.exp(
        (-0.5 * performanceDiff * performanceDiff) / performanceVariance
      ) / Math.sqrt(2 * Math.PI * performanceVariance)
    );
  }

  /**
   * Process a match and update all player ratings
   * @param {Object} match - Match object with winners, losers, and scores
   * @param {Array} players - Array of all player objects
   * @returns {Array} Updated player objects
   */
  processMatch(match, players) {
    // Find winner and loser players
    const winnerPlayers = players.filter(p => match.winners.includes(p.id));
    const loserPlayers = players.filter(p => match.losers.includes(p.id));

    // Extract current ratings
    const winnerRatings = winnerPlayers.map(p => ({
      mu: p.mu,
      sigma: p.sigma,
    }));
    const loserRatings = loserPlayers.map(p => ({ mu: p.mu, sigma: p.sigma }));

    // Calculate winner/loser scores from match data
    const winnerScore = Math.max(match.team1Score || 0, match.team2Score || 0);
    const loserScore = Math.min(match.team1Score || 0, match.team2Score || 0);

    // Calculate new ratings with score margin
    const { winners: newWinnerRatings, losers: newLoserRatings } =
      this.rate2v2Match(winnerRatings, loserRatings, winnerScore, loserScore);

    // Update player objects
    const updatedPlayers = players.map(player => {
      const winnerIndex = winnerPlayers.findIndex(p => p.id === player.id);
      const loserIndex = loserPlayers.findIndex(p => p.id === player.id);

      if (winnerIndex >= 0) {
        return {
          ...player,
          mu: newWinnerRatings[winnerIndex].mu,
          sigma: newWinnerRatings[winnerIndex].sigma,
          wins: player.wins + 1,
        };
      } else if (loserIndex >= 0) {
        return {
          ...player,
          mu: newLoserRatings[loserIndex].mu,
          sigma: newLoserRatings[loserIndex].sigma,
          losses: player.losses + 1,
        };
      } else {
        return player;
      }
    });

    return updatedPlayers;
  }

  /**
   * Calculate expected win probability for team1 vs team2
   */
  calculateWinProbability(team1Ratings, team2Ratings) {
    const team1Stats = this.calculateTeamStats(team1Ratings);
    const team2Stats = this.calculateTeamStats(team2Ratings);

    const performanceDiff = team1Stats.mu - team2Stats.mu;
    const performanceStd = Math.sqrt(
      team1Stats.sigma * team1Stats.sigma + team2Stats.sigma * team2Stats.sigma
    );

    return this.normCdf(0, -performanceDiff, performanceStd);
  }
}

// Export singleton instance
export const trueSkillCalculator = new TrueSkillCalculator();

// Export class for testing
export { TrueSkillCalculator };
